// Simple User Menu component for authentication status
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { upgradeToPremmium } from '../../services/stripeService';
import './UserMenu.css';

interface UserMenuProps {
  className?: string;
  onShowLoginModal?: () => void;
  onShowSubscriptionManager?: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ 
  className = '', 
  onShowLoginModal, 
  onShowSubscriptionManager 
}) => {
  const { user, signOut, loading } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
    } catch {
      // Handle sign out error silently
    }
  };

  const handleUpgrade = async () => {
    if (!user) return;
    
    try {
      setShowUserMenu(false);
      await upgradeToPremmium(user);
    } catch {
      // Show user feedback on error
      alert('Failed to start upgrade process. Please try again.');
    }
  };

  const handleManageSubscription = () => {
    setShowUserMenu(false);
    onShowSubscriptionManager?.();
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showUserMenu]);

  if (loading) {
    return (
      <div className={`user-menu ${className}`}>
        <div className="user-menu-loading">
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`user-menu ${className}`}>
        <button 
          className="sign-in-btn"
          onClick={() => onShowLoginModal?.()}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div className={`user-menu ${className}`}>
      <div className="user-menu-container" ref={dropdownRef}>
        <button 
          className="user-avatar-btn"
          onClick={() => setShowUserMenu(!showUserMenu)}
        >
          {user.photoURL ? (
            <img 
              src={user.photoURL} 
              alt={user.displayName || 'User'} 
              className="user-avatar"
            />
          ) : (
            <div className="user-avatar-placeholder">
              {(user.displayName || user.email || 'U')[0].toUpperCase()}
            </div>
          )}
          <span className="user-tier-badge tier-{user.tier}">
            {user.tier === 'premium' ? 'PRO' : 'FREE'}
          </span>
        </button>

        {showUserMenu && (
          <div className="user-dropdown">
            <div className="user-info">
              <div className="user-name">
                {user.displayName || 'Guitar Player'}
              </div>
              <div className="user-email">
                {user.email}
              </div>
              <div className="user-tier">
                {user.tier === 'premium' ? 'Premium Account' : 'Free Account'}
              </div>
            </div>
            
            {user.tier === 'free' && (
              <button 
                className="upgrade-btn"
                onClick={handleUpgrade}
              >
                ⭐ Upgrade to Premium
              </button>
            )}

            {user.tier === 'premium' && (
              <button 
                className="manage-subscription-btn"
                onClick={handleManageSubscription}
              >
                Manage Subscription
              </button>
            )}
            
            <div className="menu-divider"></div>
            
            <button 
              className="sign-out-btn"
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
